import { useAuth } from "../../lib/auth";
import { useApiData } from "../../contexts/ApiData";
import { useTranslations } from "../../contexts/Translation";
import { useEffect, useState } from "react";
import { Organization, SettingsFormProps, ValidationError, ValidationResult } from "../../types";
import Input from "./Input";
import ImageUploader from "./ImageUpload/ImageUploader";
import LogoImage from "../LogoImage";
import ErrorText from "./ErrorText";
import { useOrganizations } from "../../contexts/UserProvider";
import Button from "../Button/Button";
import { H2, H3 } from "../Headers/Headings";
import Link from "../Link/Link";
import { InfoMessage } from "../InfoMessage/InfoMessage";
import Card from "../Card/Card";
import PricingPlans from "../PricingPlans/PricingPlans";
import styles from "./FormStyles.module.css";

type OrganizationFormData = {
  id: string;
  name: string;
  orgNo: string;
  country: string;
  domain: string;
  logo?: string;
  consentToTerms: number;
  consentToAgreement: number;
  allowPrivateSurveys: number;
};

const OrganizationForm = ({ isEdit = false }: SettingsFormProps) => {
  const t = useTranslations("orgForm");
  const { user } = useAuth();
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [receipt, setReceipt] = useState(false);
  const [showUploader, setShowUploader] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const { currentOrganization } = useOrganizations();
  const { post, put, get } = useApiData();
  const [formData, setFormData] = useState<OrganizationFormData>({
    id: crypto.randomUUID(),
    name: "",
    orgNo: "",
    country: "",
    domain: user?.email?.split("@")[1] ?? "",
    logo: "",
    consentToTerms: 0,
    consentToAgreement: 0,
    allowPrivateSurveys: 1,
  });

  useEffect(() => {
    const call = async () => {
      const response = await get("/organization");
      if (response) {
        const currentOrg = response.find((o: Organization) => o.id === currentOrganization);
        setFormData({ ...formData, ...currentOrg });
      }
    };

    if (isEdit) {
      call();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization]);

  const validateData = ({ name, consentToTerms, consentToAgreement }: OrganizationFormData): ValidationResult => {
    const errors: ValidationError[] = [];
    if (!name) {
      errors.push({ name: "name", message: t("fieldRequired") });
    }
    if (!consentToTerms) {
      errors.push({ name: "consentToTerms", message: t("consentToTermsRequired") });
    }
    if (!consentToAgreement) {
      errors.push({ name: "consentToAgreement", message: t("consentToAgreementRequired") });
    }

    return { errors: errors.length > 0 ? errors : undefined, success: errors.length === 0 };
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReceipt(false);
    setErrors(errors.filter((err) => err.name !== e.target.name));
    setFormData({
      ...formData,
      [e.target.name]: e.target.type === "checkbox" ? (e.target.checked ? 1 : 0) : e.target.value,
    });
  };

  const updateLogo = (dataUrl: string) => {
    setFormData({ ...formData, logo: dataUrl });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationResult = validateData(formData);
    const apiFn = isEdit ? put : post;

    if (validationResult.success) {
      const result = await apiFn(`/organization/${formData.id}`, formData);
      if (!result) {
        setErrors([{ name: "form", message: t("serverError") }]);
      } else {
        if (!isEdit) {
          setShowPayment(true);
          window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          setReceipt(true);
          window.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        }
      }
    } else {
      setErrors(validationResult.errors || []);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  if (showPayment) {
    return <PricingPlans afterOrgRegistration />;
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <H2>{t(isEdit ? "updateTitle" : "title")}</H2>
      {receipt && <p className={styles.receipt}>{t("receiptText")}</p>}
      {errors.filter((e) => e.name !== "form").length > 0 && <ErrorText variant='form' text={t("formErrors")} />}
      <ErrorText variant='form' error={errors.find((e) => e.name === "form")} />
      <Card className={styles.cardSection} padding='large' variant='fixed'>
        <div className={styles.fieldRow}>
          <Input error={errors.find((e) => e.name === "name")} name='name' handleChange={handleChange} label={t("orgName")} value={formData.name} />
        </div>
      </Card>
      <H3>{t("logoLabel")}</H3>
      <Card className={styles.cardSection} padding='large' variant='fixed'>
        <div className={styles.mediaRow}>
          <LogoImage
            src={formData.logo && !formData.logo.startsWith("data:") ? formData.logo : ""}
            dataUrl={formData.logo && formData.logo.startsWith("data:") ? formData.logo : undefined}
            alt={t("logoAlt")}
            maxHeight={80}
            maxWidth={80}
          />
          <Button type='button' onClick={() => setShowUploader(true)}>
            {t("uploadLogo")}
          </Button>
        </div>
        {showUploader && (
          <ImageUploader
            maxWidth={512}
            maxHeight={512}
            aspectRatios={["square"]}
            defaultAspect='square'
            original={formData.logo || undefined}
            onImageReady={(base64) => {
              updateLogo(base64);
              setShowUploader(false);
            }}
            onClose={() => setShowUploader(false)}
          />
        )}
      </Card>
      <Card className={styles.cardSection} padding='large' variant='fixed'>
        <div className={styles.fieldRow}>
          <Input
            type='checkbox'
            error={errors.find((e) => e.name === "consentToAgreement")}
            name='consentToAgreement'
            handleChange={handleChange}
            label={t("consentToAgreement")}
            value={formData.consentToAgreement}
            infoText={
              <>
                <Link target='_blank' href='https://feedbackjourney.com/agreement'>
                  {t("agreementLink")}
                </Link>
              </>
            }
          />
          <Input
            type='checkbox'
            error={errors.find((e) => e.name === "consentToTerms")}
            name='consentToTerms'
            handleChange={handleChange}
            label={t("consentToTerms")}
            value={formData.consentToTerms}
            infoText={
              <>
                <Link target='_blank' href='https://feedbackjourney.com/agreement/terms'>
                  {t("termsLink")}
                </Link>
              </>
            }
          />
        </div>
      </Card>
      <H2>{t("orgSettingsHeading")}</H2>
      <Card className={styles.cardSection} padding='large' variant='fixed'>
        <div className={styles.fieldRow}>
          <Input
            type='checkbox'
            error={errors.find((e) => e.name === "allowPrivateSurveys")}
            name='allowPrivateSurveys'
            handleChange={handleChange}
            label={t("allowPrivateSurveys")}
            value={formData.allowPrivateSurveys}
            infoText={<InfoMessage>{t("allowPrivateSurveysInfo")}</InfoMessage>}
          />
        </div>
      </Card>
      <Button>{t("submit")}</Button>
    </form>
  );
};

export default OrganizationForm;
